import {Measurements, measurementSides, measurementsYOffset} from "./measurements";
import {Box3} from "three";
import {Dimension} from "./dimension";
import {getUniqueFloatArray, toFixedFloat} from "../../utils/utils";
import {measurementsGroup} from "../../main";
import {findChildByName} from "../../utils/threeUtils";
import {getSupportBeamsByOrientation, getWallGroupsByOrientation} from "../../utils/configuratorUtils";
import {sortFloatArray} from "../../utils/mathUtils";
import {getIntersectingWalls} from "../walls/wall";
import {controlObjectName, cutOutsGroupName} from "../../constants/nameConstants";

const defaultOpeningMeasurementOffset = 0.7;
const defaultInnerWallsMeasurementOffset = 1.5;
const defaultOuterWallsMeasurementOffset = 2.3;

export class WorkingMeasurements extends Measurements {
    constructor() {
        try {
            super();
            //Nur generieren, wenn mindestens 4 Außenwände vorhanden sind. Darunter nicht sinnvoll
            if (this.outerWalls.length < 4) {
                return; //TODO Ggf. erweitern auf nur wenn ein geschlossener Außenwand Loop vorliegt
            }

            this.sortWallsBySide(); //Set this.walls from all outer walls

            this.createOutsideMeasurements();
            this.createInsideMeasurements();
            this.createOpeningMeasurements();
        } catch (e) {
            console.error(e);
        }
    }

    createOutsideMeasurements() {
        this.createOutSideMeasurementForSide(this.walls.left, measurementSides.left);
        this.createOutSideMeasurementForSide(this.walls.right, measurementSides.right);
        this.createOutSideMeasurementForSide(this.walls.front, measurementSides.front);
        this.createOutSideMeasurementForSide(this.walls.back, measurementSides.back);
    }

    createInsideMeasurements() {
        this.createInsideMeasurementForSide(this.walls.left, measurementSides.left);
        this.createInsideMeasurementForSide(this.walls.right, measurementSides.right);
        this.createInsideMeasurementForSide(this.walls.front, measurementSides.front);
        this.createInsideMeasurementForSide(this.walls.back, measurementSides.back);
    }

    createOpeningMeasurements() {
        this.createOpeningMeasurementForSide(this.walls.left, measurementSides.left);
        this.createOpeningMeasurementForSide(this.walls.right, measurementSides.right);
        this.createOpeningMeasurementForSide(this.walls.front, measurementSides.front);
        this.createOpeningMeasurementForSide(this.walls.back, measurementSides.back);
    }


    //Outside measurements functions
    createOutSideMeasurementForSide(walls, side) {
        let points = this.getDimensionPoints(walls, side, true);
        let dimension = this.createMeasurementForSide(walls, side, points);
        this.positionDimension(dimension, side, defaultOuterWallsMeasurementOffset);
        measurementsGroup.addWithUuid(dimension);
    }

    getDimensionPoints(walls, side, maximize = false) {
        let useX = side === measurementSides.back || side === measurementSides.front;
        let points = [];

        for (let i = 0; i < walls.length; i++) {
            let bounds = new Box3().setFromObject(walls[i]);

            let newPoints = [];
            newPoints.push(toFixedFloat(useX ? bounds.min.x : bounds.min.z));
            newPoints.push(toFixedFloat(useX ? bounds.max.x : bounds.max.z));

            let otherWalls = [];
            if (side === measurementSides.back || side === measurementSides.front) {
                otherWalls = otherWalls.concat(this.walls.left);
                otherWalls = otherWalls.concat(this.walls.right);
            } else if (side === measurementSides.left || side === measurementSides.right) {
                otherWalls = otherWalls.concat(this.walls.back);
                otherWalls = otherWalls.concat(this.walls.front);
            }

            newPoints = newPoints.concat(this.getDimensionPointsForIntersectingWalls([walls[i]], side, otherWalls));//Suche zu jeder Wand die schneidenen Wände > Bounds dazu nehmen und dann maximieren
            if (!maximize) {
                points = points.concat(newPoints);
            } else {
                points = points.concat(this.getMaximizedPoints(points, newPoints));
            }
        }

        return points;
    }

    getDimensionPointsForIntersectingWalls(walls, side, otherWalls) {
        let points = [];
        let useX = side === measurementSides.back || side === measurementSides.front;

        if (otherWalls.length > 0) {
            let intersectingWalls = [];
            for (let i = 0; i < walls.length; i++) {
                intersectingWalls = intersectingWalls.concat(getIntersectingWalls(walls[i], otherWalls, false, false, false, true));
            }

            walls = walls.concat(intersectingWalls);
        }

        for (let i = 0; i < walls.length; i++) {
            let bounds = new Box3().setFromObject(walls[i]);
            points.push(toFixedFloat(useX ? bounds.min.x : bounds.min.z));
            points.push(toFixedFloat(useX ? bounds.max.x : bounds.max.z));
        }

        return points;
    }


    //Cutouts measurements functions
    createOpeningMeasurementForSide(walls, side) {
        let points = this.getDimensionPointsWithOpenings(walls, side);
        let dimension = this.createMeasurementForSide(walls, side, points);
        this.positionDimension(dimension, side, defaultOpeningMeasurementOffset);
        measurementsGroup.addWithUuid(dimension);
    }

    getDimensionPointsWithOpenings(walls, side) {
        let useX = side === measurementSides.back || side === measurementSides.front;
        let points = [];

        for (let i = 0; i < walls.length; i++) {
            let bounds = new Box3().setFromObject(walls[i]);
            points.push(toFixedFloat(useX ? bounds.min.x : bounds.min.z));
            points.push(toFixedFloat(useX ? bounds.max.x : bounds.max.z));
            points = points.concat(this.getOpeningDimensions(walls[i], useX));
        }

        return points;
    }

    getOpeningDimensions(wall, useX) {
        let points = [];

        let cutOutsGroup = findChildByName(wall, cutOutsGroupName);
        if (cutOutsGroup !== null && cutOutsGroup.children.length > 0) {
            for (let i = 0; i < cutOutsGroup.children.length; i++) {
                let cutOut = cutOutsGroup.children[i];
                let controlObject = findChildByName(cutOut, controlObjectName);
                let bounds = new Box3().setFromObject(controlObject);
                points.push(toFixedFloat(useX ? bounds.min.x : bounds.min.z));
                points.push(toFixedFloat(useX ? bounds.max.x : bounds.max.z));
            }
        }

        return points;
    }


    //Inside measurements functions
    createInsideMeasurementForSide(walls, side) {
        let points = this.getInnerWallDimensionPoints(walls, side, false);
        let dimension = this.createMeasurementForSide(walls, side, points);
        this.positionDimension(dimension, side, defaultInnerWallsMeasurementOffset);
        measurementsGroup.addWithUuid(dimension);
    }

    getInnerWallDimensionPoints(walls, side) {
        let useX = side === measurementSides.back || side === measurementSides.front;
        let points = [];

        for (let i = 0; i < walls.length; i++) {
            let bounds = new Box3().setFromObject(walls[i]);
            points.push(toFixedFloat(useX ? bounds.min.x : bounds.min.z));
            points.push(toFixedFloat(useX ? bounds.max.x : bounds.max.z));

            let otherWalls = [];
            if (side === measurementSides.back || side === measurementSides.front) {
                otherWalls = getWallGroupsByOrientation("z").concat(getSupportBeamsByOrientation("z"));
            } else if (side === measurementSides.left || side === measurementSides.right) {
                otherWalls = getWallGroupsByOrientation("x").concat(getSupportBeamsByOrientation("x"));
            }

            points = points.concat(this.getDimensionPointsForIntersectingWalls([walls[i]], side, otherWalls));//Suche zu jeder Wand die schneidenen Wände > Bounds dazu nehmen und dann maximieren
        }

        return points;
    }

    //Utils
    positionDimension(dimension, side, offset) {
        switch (side) {
            case measurementSides.front:
                dimension.position.set(0, measurementsYOffset, this.outerWallsBounds.max.z + offset);
                break;
            case measurementSides.back:
                dimension.position.set(0, measurementsYOffset, this.outerWallsBounds.min.z - offset);
                break;
            case measurementSides.left:
                dimension.position.set(this.outerWallsBounds.min.x - offset, measurementsYOffset, 0);
                break;
            case measurementSides.right:
                dimension.position.set(this.outerWallsBounds.max.x + offset, measurementsYOffset, 0);
                break;
            default:
                break;
        }
    }

    createMeasurementForSide(walls, side, points) {
        points = this.padDimensionPointsWithBounds(points, side);
        points = this.filterAndSortDimensionPoints(points);
        let dimension = new Dimension(points, side === measurementSides.left || side === measurementSides.right, this.scaleFactor);

        if (side === measurementSides.left || side === measurementSides.right) {
            dimension.rotateY(Math.PI / 2);
        }

        return dimension;
    }

    getMaximizedPoints(existingPoints, newPoints) {
        let minPoint = Math.min(...newPoints);
        let maxPoint = Math.max(...newPoints);

        if (minPoint === undefined) {
            console.log("min undefined");
        }
        if (maxPoint === undefined) {
            console.log("max undefined");
        }

        if (existingPoints.length === 0) {
            return [minPoint, maxPoint];
        }

        let existingMin = Math.min(...existingPoints);
        let existingMax = Math.max(...existingPoints);

        if (existingMin > minPoint) {
            existingPoints.push(minPoint);
        }

        if (existingMax < maxPoint) {
            existingPoints.push(maxPoint);
        }

        return existingPoints;
    }

    padDimensionPointsWithBounds(points, side) {
        let useX = side === measurementSides.back || side === measurementSides.front;

        points.push(toFixedFloat(useX ? this.outerWallsBounds.min.x : this.outerWallsBounds.min.z));
        points.push(toFixedFloat(useX ? this.outerWallsBounds.max.x : this.outerWallsBounds.max.z));

        return points;
    }

    filterAndSortDimensionPoints(points) {
        points = getUniqueFloatArray(points);
        return sortFloatArray(points);
    }
}
